import {
  AlertListResponse,
  AlertListTableItem,
  AlertStats,
  AlertStatsListItem,
  AlertStatsResponse,
  GetAlertLevelsSuccessResponse,
  TransactionAlert,
  TransactionAlerts,
  UpdateTransactionAlertResponse,
} from '@/api';
import { RiskKeys } from '@/config';
import { getFormattedDateWithTime } from '@/helpers';
import { ComplianceAlertsData } from '@/redux/models';

const RISK_KEY_VALUE_SET = new Set<string>(Object.values(RiskKeys) as string[]);

const isRiskKeyString = (value: string): value is RiskKeys =>
  RISK_KEY_VALUE_SET.has(value);

const categoryEntryToRiskKey = (
  entry: string | { name: string },
): RiskKeys | null => {
  const raw = typeof entry === 'string' ? entry : entry.name;
  return isRiskKeyString(raw) ? raw : null;
};

const mapEntityCategories = (item: AlertStatsListItem): RiskKeys[] | null => {
  const direct = item.categories ?? [];
  const fromDirect: RiskKeys[] = [];
  for (const entry of direct) {
    const key = categoryEntryToRiskKey(entry);
    if (key) fromDirect.push(key);
  }
  if (fromDirect.length > 0) return fromDirect;

  const ruleCategories = item.rule?.categories ?? [];
  const fromRule: RiskKeys[] = [];
  for (const cat of ruleCategories) {
    if (cat.isActive === false) continue;
    if (isRiskKeyString(cat.name)) fromRule.push(cat.name);
  }
  return fromRule.length > 0 ? fromRule : null;
};

const fetchStats = (response: AlertStatsResponse): AlertStats => {
  const { data } = response;
  const totalByRisk = Object.values(data.byRisk).reduce(
    (total, val) => total + val,
    0,
  );

  const totalByStatus = Object.values(data.byStatus).reduce(
    (total, val) => total + val,
    0,
  );

  data.totalByRisk = totalByRisk;
  data.totalByStatus = totalByStatus;

  return data;
};

const fetchList = (response: AlertListResponse): ComplianceAlertsData => {
  const {
    data: { items, total },
  } = response;

  let entries: AlertListTableItem[] = [];

  if (Array.isArray(items) && items.length) {
    entries = items.map((item) => ({
      key: item.id,
      id: item.id,
      user: item.customer[0]?.name || item.customer[0]?.identifier || '',
      address: item.address,
      direction: item.direction,
      risk: item.risk,
      riskyFunds: item.risky ? item.risky / 100 : 0,
      entityCategory: mapEntityCategories(item),
      state: item.status,
      date: getFormattedDateWithTime(item.createdDate),
      customer: item.customer,
      behaviorTxs: item.behaviorTxs,
      rule: item.rule,
    }));
  }

  return { entries, total };
};

const fetchAlertSettings = (
  response: GetAlertLevelsSuccessResponse,
): TransactionAlerts => {
  const { data } = response;
  return data;
};

const fetchAlertLevels = (
  response: UpdateTransactionAlertResponse,
): TransactionAlerts => {
  const { categories, levels, directions, interactionTypes } = response.data;

  return {
    categories,
    levels,
    directions,
    interactionTypes,
  };
};

export { fetchStats, fetchList, fetchAlertSettings, fetchAlertLevels };
